import {
  PermissionAction,
  SEGMENT_WILDCARD,
  TAIL_WILDCARD,
  type PermissionExpression,
  type PermissionRequirement,
} from "./contracts";
import {
  isValidPermissionExpression,
  matchesPermission,
} from "./matchPermission";

export interface PermissionEvaluator {
  readonly grants: readonly PermissionExpression[];

  /** Does the user hold a grant matching this exact expression or pattern? */
  has(expression: PermissionExpression): boolean;

  /** Is the requirement met? `undefined` and empty requirements always pass. */
  satisfies(requirement?: PermissionRequirement): boolean;

  /**
   * Convenience over `has`. `screen` is the three-segment prefix
   * `<module>.<submodule>.<screen>`; `action` is the fourth segment.
   */
  can(screen: string, action: string): boolean;

  canView(screen: string): boolean;
  canCreate(screen: string): boolean;
  canUpdate(screen: string): boolean;
  canDelete(screen: string): boolean;
}

function toRequirementParts(requirement: PermissionRequirement): {
  anyOf: readonly PermissionExpression[];
  allOf: readonly PermissionExpression[];
} {
  // The bare-array shorthand is disjunctive. See PermissionRequirement docs.
  if (Array.isArray(requirement)) {
    return { anyOf: requirement, allOf: [] };
  }

  const object = requirement as {
    anyOf?: readonly PermissionExpression[];
    allOf?: readonly PermissionExpression[];
  };

  return { anyOf: object.anyOf ?? [], allOf: object.allOf ?? [] };
}

/**
 * Builds a permission evaluator over a user's granted expressions.
 *
 * Grants are split into an exact-match set and a much smaller wildcard list, so
 * the common case is a single hash lookup. Results are memoised because
 * navigation resolution asks the same questions on every render.
 */
export function createPermissionEvaluator(
  grantedExpressions: readonly PermissionExpression[] | undefined,
): PermissionEvaluator {
  const grants = (grantedExpressions ?? []).filter(isValidPermissionExpression);

  const exact = new Set<PermissionExpression>();
  const patterns: PermissionExpression[] = [];

  for (const grant of grants) {
    if (grant.includes(SEGMENT_WILDCARD) || grant.includes(TAIL_WILDCARD)) {
      patterns.push(grant);
    } else {
      exact.add(grant);
    }
  }

  const cache = new Map<PermissionExpression, boolean>();

  function has(expression: PermissionExpression): boolean {
    const cached = cache.get(expression);

    if (cached !== undefined) {
      return cached;
    }

    let result = false;

    if (
      !expression.includes(SEGMENT_WILDCARD) &&
      !expression.includes(TAIL_WILDCARD)
    ) {
      result = exact.has(expression);
    }

    if (!result) {
      result =
        patterns.some((pattern) => matchesPermission(pattern, expression)) ||
        // The request itself may be a pattern, e.g. "does the user hold any
        // action on this screen?" — check it against concrete grants too.
        (expression.includes(SEGMENT_WILDCARD) &&
          grants.some((grant) => matchesPermission(grant, expression)));
    }

    cache.set(expression, result);

    return result;
  }

  function satisfies(requirement?: PermissionRequirement): boolean {
    if (requirement === undefined) {
      return true;
    }

    const { anyOf, allOf } = toRequirementParts(requirement);

    if (anyOf.length === 0 && allOf.length === 0) {
      return true;
    }

    if (allOf.length > 0 && !allOf.every(has)) {
      return false;
    }

    if (anyOf.length > 0 && !anyOf.some(has)) {
      return false;
    }

    return true;
  }

  function can(screen: string, action: string): boolean {
    return has(`${screen}.${action}`);
  }

  return {
    grants,
    has,
    satisfies,
    can,
    canView: (screen) => can(screen, PermissionAction.View),
    canCreate: (screen) => can(screen, PermissionAction.Create),
    canUpdate: (screen) => can(screen, PermissionAction.Update),
    canDelete: (screen) => can(screen, PermissionAction.Delete),
  };
}

export const EMPTY_PERMISSION_EVALUATOR = createPermissionEvaluator([]);
